const DOMAIN = 'xic12r-zi.myshopify.com';
const TOKEN = '18c5e60b188a9e01c64028e2264bd157';
const API = `https://${DOMAIN}/api/2024-01/graphql.json`;

// Live product IDs (created 2026-07-12):
// - brackets:        Star Picket Brackets (supported). Two variants on a
//   "Support Angle" option (both share the same 2.0 mm steel sleeve):
//     · "2.5 mm" — variant 46567656194102, $7.50, the Standard entry spec.
//     · "3.0 mm" — variant 46934713073718, $7.95, the Heavy Duty premium upgrade
//       (thicker Duragal angle). Both are buyable (CONTINUE policy / pre-order).
//   Always pass an explicit variantTitle (see ANGLE_VARIANT_TITLE) rather than
//   relying on variant order.
// - flatBracket:     StakeTek Flat Slide-On Bracket, variant 46858002235446, $7.00
// - installationKits: StakeTek Installation Kit — 12 variants named exactly as
//   kitVariantTitle() in data/hardware.js ("Standard — 1 bracket kit" ...
//   "Plinth — 50 bracket kit"), $1.50/$12/$23/$33/$42/$50.
// - angleUpgrade:    2.5 mm Support Angle Upgrade — RETIRED 2026-07-20, now DRAFT.
//   The 2.5 mm price is built into the variant above, so this is no longer added
//   as a cart line. Kept (not deleted) so historical orders still resolve.
// Bulk pack discounts are live as Shopify automatic discounts — $0.15/$0.30/$0.40/
// $0.45/$0.50 off EACH bracket at 10/20/30/40/50+, applied to both brackets and
// flatBracket. Shopify applies only one automatic discount per order, which is why
// both products share one set. Keep in sync with the tier tables in lib/pricing.js.
// Star Pickets, Hard Ground Pointer and Animal Extension were created 2026-07-13
// as DRAFT products (Phase 4). They will not price or check out through the
// Storefront API until they are set to ACTIVE and published to the Online Store
// sales channel, and real inventory is set. Until then the picket/extension
// pages fall back to local provisional pricing (data/pickets.js).
export const SHOPIFY_PRODUCTS = {
  brackets: '8810002186294',
  flatBracket: '8978511364150',
  installationKits: '8978511495222',
  pickets: '8984400003126',        // StakeTek Star Pickets — 12 variants (Length × Finish), DRAFT
  pointer: '8984400035894',        // StakeTek Hard Ground Pointer — add-on, $2.25, DRAFT
  animalExtension: '8984400101430', // StakeTek Animal Extension — Black/Gal Steel, $6.00, DRAFT
  angleUpgrade: '8990084399158',   // 2.5 mm Support Angle Upgrade — RETIRED (DRAFT), no longer sold
  // Fence Height Extensions — LIVE since 2026-09-05. Each is a separate product
  // with ONE "Length" option and three variants titled "450 mm", "600 mm" and
  // "900 mm" (see extensionVariantTitle in data/fenceExtensions.js — the cart
  // resolves the variant by that exact title). Both ACTIVE and published to the
  // Online Store, untracked inventory with CONTINUE policy. Prices and shipping
  // weights live in data/fenceExtensions.js and must stay in sync with Shopify.
  galvanisedExtension: '9257503653942', // galvanised 8.35 / 9.00 / 11.00
  aluminiumExtension: '9285191827510',  // aluminium  6.45 / 6.95 / 8.95
  // Star Picket Animal Extension (built in code 2026-08-05) — galvanised Y-pipe
  // sleeve that slides over a standard star picket and bolts on. SINGLE product,
  // ONE variant, no size option, $22.50 each (see data/animalExtensions.js).
  // Same "checkout being finalised" fallback until this id is set.
  starPicketAnimalExtension: null, // REQUIRED — StakeTek Star Picket Animal Extension
  // Anchor Base Bracket (scaffolded 2026-08-14) — SINGLE product, one
  // variant, sale-ready page. Ships at bracket weight or lighter. Set this id +
  // the confirmed price in data/concreteMount.js to turn checkout on.
  concreteMount: null,             // REQUIRED — StakeTek Anchor Base Bracket
  // Pre-Drilled Galvanised Steel Angle (scaffolded 2026-08-14) — Components &
  // Accessories. Single variant (add a Length/Pack option only if real). Set
  // this id + the confirmed price in data/steelAngle.js to turn checkout on.
  steelAngle: null,                // REQUIRED — Pre-Drilled Galvanised Steel Angle
};

// Variant titles on the Star Picket Brackets product, keyed by support-angle id
// (see data/supportAngle.js). Passed as variantTitle so the cart always resolves
// the intended angle instead of whichever variant happens to be first.
export const ANGLE_VARIANT_TITLE = { standard: '2.5 mm', heavy: '3.0 mm' };

// Variant title format for the Star Picket product = "<Length> / <Finish>",
// e.g. "1800 mm / Black" — matches how Shopify names two-option variants and is
// what getVariantId() looks up when building the cart.
export const picketVariantTitle = (lengthMm, finish) => `${lengthMm} mm / ${finish}`;

async function gql(query, variables = {}) {
  const res = await fetch(API, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  if (json.errors) throw new Error(json.errors[0].message);
  return json;
}

// Resolves a variant ID for a product. With no variantTitle it returns the
// first variant (single-variant products like the brackets). With a
// variantTitle it matches the variant whose title equals it (used for the
// Installation Kit sizes).
export async function getVariantId(productId, variantTitle) {
  const data = await gql(`
    query GetVariants($id: ID!) {
      product(id: $id) {
        variants(first: 20) {
          edges { node { id title } }
        }
      }
    }
  `, { id: `gid://shopify/Product/${productId}` });

  const edges = data?.data?.product?.variants?.edges;
  if (!edges?.length) throw new Error('No variants found');
  if (!variantTitle) return edges[0].node.id;
  const match = edges.find(
    (edge) => edge.node.title.trim().toLowerCase() === variantTitle.trim().toLowerCase()
  );
  if (!match) throw new Error(`Variant "${variantTitle}" not found`);
  return match.node.id;
}

// A recorded variant id skips getVariantId() entirely. That lookup is a whole
// extra round trip to Shopify before the cart call can even start (~390 ms of a
// ~1.15 s "Add to cart"), so any page that already knows its variant id should
// pass it as `variantId` on the cart line.
export const variantGid = (id) =>
  String(id).startsWith("gid://") ? String(id) : `gid://shopify/ProductVariant/${id}`;

/**
 * Build the cart lines for a set of items.
 * With useRecordedIds (the default) a line carrying `variantId` resolves with no
 * network call; everything else still resolves by variant title. Pass false to
 * force the title lookup, which is how callers retry if a recorded id has gone
 * stale in Shopify.
 */
export async function resolveCartLines(items, useRecordedIds = true) {
  return Promise.all(
    items.map(async (item) => {
      const merchandiseId =
        useRecordedIds && item.variantId
          ? variantGid(item.variantId)
          : await getVariantId(item.productId, item.variantTitle);
      const line = { merchandiseId, quantity: item.quantity };
      if (item.attributes?.length) line.attributes = item.attributes;
      return line;
    })
  );
}

// True when at least one line could fall back to a title lookup, i.e. a retry
// would actually resolve things differently.
export const hasRecordedIds = (items) => items.some((item) => item.variantId);

/**
 * Fetch the live retail price for a product from Shopify (so the site never
 * hardcodes pricing and always matches the store). Returns { amount, currencyCode }.
 */
export async function getProductPrice(productId) {
  const data = await gql(`
    query GetPrice($id: ID!) {
      product(id: $id) {
        priceRange {
          minVariantPrice { amount currencyCode }
        }
      }
    }
  `, { id: `gid://shopify/Product/${productId}` });

  const price = data?.data?.product?.priceRange?.minVariantPrice;
  if (!price) throw new Error('No price found');
  return price; // { amount: "29.95", currencyCode: "AUD" }
}

/**
 * Live availability for a product from the Storefront API. Returns
 * { availableForSale, totalInventory } or null when the product isn't published
 * to the storefront yet (e.g. still a DRAFT), so callers can fall back to a
 * neutral "made to order" status instead of a hardcoded "in stock".
 */
export async function getProductAvailability(productId) {
  try {
    // Storefront API Product exposes `availableForSale` (true when any variant is
    // buyable). `totalInventory` is Admin-only and would error here. A DRAFT
    // product isn't published to the storefront, so `product` comes back null and
    // the caller shows its neutral fallback rather than a false "in stock".
    const data = await gql(`
      query GetAvailability($id: ID!) {
        product(id: $id) {
          availableForSale
        }
      }
    `, { id: `gid://shopify/Product/${productId}` });
    const p = data?.data?.product;
    if (!p) return null;
    return { availableForSale: Boolean(p.availableForSale) };
  } catch {
    return null;
  }
}

/**
 * Creates a Shopify cart from one or more lines and redirects to checkout.
 * items = [{ productId, quantity, variantTitle?, attributes? }]
 * Each line stays separate in the cart so the order is clear (e.g. brackets
 * plus a matched installation kit).
 */
export async function buyLines(items) {
  let lines = await resolveCartLines(items);

  // Cart API — modern replacement for deprecated checkoutCreate
  const create = (input) => gql(`
    mutation CartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart { checkoutUrl }
        userErrors { message }
      }
    }
  `, { input });

  let data = await create({ lines });
  let errors = data?.data?.cartCreate?.userErrors;

  // A recorded variant id that no longer exists in Shopify would fail here.
  // Retry once resolving every line the slow way rather than dead-ending a
  // customer who is trying to pay.
  if (errors?.length && hasRecordedIds(items)) {
    lines = await resolveCartLines(items, false);
    data = await create({ lines });
    errors = data?.data?.cartCreate?.userErrors;
  }
  if (errors?.length) throw new Error(errors[0].message);

  const url = data?.data?.cartCreate?.cart?.checkoutUrl;
  if (!url) throw new Error('No checkout URL returned');

  window.location.href = url;
}

export async function buyNow(productId, quantity = 1, finish) {
  // Same price for both finishes, so capture the choice as a cart line attribute
  // (carries to the Shopify order) rather than a separate variant.
  const attributes = finish ? [{ key: "Finish", value: finish }] : undefined;
  await buyLines([{ productId, quantity, attributes }]);
}
